import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiRequestError, api, setUnauthenticatedHandler, tokenStore } from './lib/api';
import { getIdToken, initLiff, isLoggedIn, login as liffLogin } from './lib/liff';

export interface DonorProfile {
  id: string;
  donorCode: string;
  displayName: string;
  isNew: boolean;
}

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: DonorProfile }
  | { kind: 'error'; error: unknown };

interface AuthState {
  status: Status;
  retry: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * ผู้บริจาคไม่ต้องสมัครล่วงหน้า — เปิด LIFF ครั้งแรกระบบสร้างบัญชีให้เลย
 * ประชาชนที่เพิ่งเจอ OA ต้องบริจาคได้ทันทีโดยไม่มีขั้นตอนกั้น
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const authenticate = useCallback(async () => {
    setStatus({ kind: 'loading' });
    try {
      await initLiff();
      if (!isLoggedIn()) {
        liffLogin();
        return;
      }

      const idToken = getIdToken();
      if (!idToken) throw new ApiRequestError('LINE_VERIFY_FAILED', 401);

      const res = await api<{ token: string; profile: DonorProfile }>('/auth/liff', {
        method: 'POST',
        anonymous: true,
        body: { idToken, app: 'donor' },
      });

      tokenStore.set(res.token);
      setStatus({ kind: 'ready', profile: res.profile });
    } catch (err) {
      setStatus({ kind: 'error', error: err });
    }
  }, []);

  useEffect(() => {
    void authenticate();
  }, [authenticate, attempt]);

  useEffect(() => {
    setUnauthenticatedHandler(() => setAttempt((v) => v + 1));
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, retry: () => setAttempt((v) => v + 1) }),
    [status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth ต้องอยู่ภายใน AuthProvider');
  return ctx;
}

export function useDonor(): DonorProfile {
  const { status } = useAuth();
  if (status.kind !== 'ready') throw new Error('ยังไม่ได้ยืนยันตัวตน');
  return status.profile;
}
